import { OnlineCourseTab, TabStatus } from "@onair/constants";
import {
	LessonDTO,
	OnlineCourseDTO,
	OnlineCourseStatus,
	ProductDTO,
	SectionDTO,
	UpsertSectionDto,
} from "@onair/repositories";
import cuid from "cuid";
import debounce from "lodash/debounce";
import { StoreApi } from "zustand";
import {
	Action,
	IOnlineCourseUpsertStore,
	IProductChange,
	SyncTabStatusOptions,
} from "./interface";

export enum PricingTypeEnum {
	FREE = "free",
	PAID = "paid",
}

const debounceMap = new Map();

// IMPORTANT: lastUpdated must be updated when any changes are made to the store otherwise the changes will not be saved
export const attachActions = (
	set: StoreApi<IOnlineCourseUpsertStore>["setState"],
	get: StoreApi<IOnlineCourseUpsertStore>["getState"],
) => ({
	publishCourse: () => {},

	updateCourse: (payload: OnlineCourseDTO) => {
		set((state) => {
			if (!payload) return state;
			const { changes, course } = state;
			const existingCourse = changes.course || state.course;

			const updatedCourse = {
				...payload,
				id: existingCourse?.id || cuid(),
				benefit: payload.benefit || "[]",
				archived: payload.archived || "[]",
				questionAndAnswers: payload.questionAndAnswers || "[]",
				tags: payload.tags || "[]",
			};
			return {
				course: {
					...course,
					...updatedCourse,
				},
				changes: {
					...changes,
					course: updatedCourse,
				},
				lastUpdated: new Date(),
			};
		});
	},
	updateSection: (payload: UpsertSectionDto) => {
		const { thumbnail, ...restSection } = payload;
		const sectionData = {
			...restSection,
			thumbnail: thumbnail
				? JSON.stringify({
						id: thumbnail.id,
						publicUrl: thumbnail.publicUrl,
						thumbnailUrl: thumbnail.thumbnailUrl,
					})
				: null,
		};

		set((state) => {
			const { changes, course } = state;
			if (!course) {
				return state;
			}
			const { section } = changes;

			// change.section is object,
			// course.sections is array
			// add new section to exist array  course.sections at position index payload.index
			const originSection = course.sections;
			const isExist = originSection?.find((item) => item.id === sectionData.id);
			if (isExist) {
				originSection?.forEach((item, index) => {
					if (item.id === sectionData.id) {
						originSection[index] = sectionData;
					}
				});
			} else {
				originSection?.splice(sectionData.index, 0, sectionData);
			}
			return {
				changes: {
					...changes,
					section: {
						...section,
						[sectionData.id]: sectionData,
					},
				},
				lastUpdated: new Date(),
				course: {
					...course,
					sections: originSection,
				},
			};
		});
	},
	updateSections: (payload: UpsertSectionDto[]) => {
		console.log("UPDATE SECTIONS", payload);
		set((state) => {
			const { changes, course } = state;
			if (!course) {
				return state;
			}
			const section = changes.section || {};
			const originSection = course.sections || [];

			const newSections = originSection.map((item) => {
				const findItem = payload.find((i) => i.id === item.id);
				if (findItem) {
					return { ...item, ...findItem };
				}
				return item;
			});
			payload.forEach((item) => {
				section[item.id] = item;
			});

			console.log("NEW SECTION CHANGE", {
				...changes,
				section: section,
			});

			return {
				changes: {
					...changes,
					section: section,
				},
				lastUpdated: new Date(),
				course: {
					...course,
					sections: newSections.sort((a, b) => a.index - b.index),
				},
			};
		});
	},

	handleDeleteSection: (id: string) => {
		set((state) => {
			const { deleteSections, course, changes } = state;
			const { section } = changes;
			// if section is new, no need to delete
			if (section[id] && section[id].isNew) {
				const { [id]: _, ...rest } = section;
				return {
					changes: {
						...changes,
						section: rest,
					},
					lastUpdated: new Date(),
				};
			}

			if (!course || !id) {
				return state;
			}

			let originSections = course.sections || [];
			// if not new but have changed, delete in change and push to delete list
			originSections = originSections.filter((item) => item.id !== id);

			if (section[id]) {
				const { [id]: _, ...rest } = section;
				return {
					course: {
						...course,
						sections: originSections,
					},
					changes: {
						...changes,
						section: rest,
					},
					deleteSections: [...deleteSections, id],
					lastUpdated: new Date(),
				};
			}
			// if not new and not changed, push to delete list

			return {
				course: {
					...course,
					sections: originSections,
				},
				deleteSections: [...deleteSections, id],
				lastUpdated: new Date(),
			};
		});
	},
	handleDeleteLesson: ({ id, sectionId, index, isNew }: LessonDTO) => {
		set((state) => {
			const { deleteLessons, changes, course, setCurrentLesson } = state;
			const { lesson } = changes;
			//delete lesson from course

			const sections = course.sections || [];
			const sectionToUpdate = sections.find((item) => item.id === sectionId);
			if (!sectionToUpdate || index === undefined || index < 0) {
				return state;
			}

			const currentSection = sections.find((item) => item.id === sectionId);
			// backward or toward or null
			const lessons = currentSection?.lessons || [];

			const lessonIndex = lessons.findIndex((item) => item.id === id);
			if (lessons.length === 1) {
				setCurrentLesson(null);
			} else {
				if (lessonIndex === lessons.length - 1) {
					setCurrentLesson(lessons[lessonIndex - 1]);
				} else {
					setCurrentLesson(null);
					setCurrentLesson({ ...lessons[lessonIndex + 1], index: lessonIndex });
				}
			}

			if (!id) {
				/// remove by index in changes

				Object.keys(lesson).forEach((key) => {
					if (lesson[key].index === index) {
						delete lesson[key];
					}
				});

				const newSections = sections.map((item) => {
					if (item.id === sectionId) {
						return {
							...item,
							lessons: item?.lessons?.filter((_, i) => i !== index),
						};
					}
					return item;
				});

				return {
					course: {
						...course,
						sections: newSections,
					},
					changes: {
						...changes,
						lesson: lesson,
					},
					lastUpdated: new Date(),
				};
			}

			const findLesson = sectionToUpdate.lessons.findIndex(
				(item) => item.id === id,
			);
			if (findLesson === -1) {
				return state;
			}

			sectionToUpdate.lessons.splice(findLesson, 1);
			// update
			const newSections = sections.map((item) => {
				if (item.id === sectionId) {
					return sectionToUpdate;
				}
				return item;
			});

			// if lesson is new, no need to delete
			if (lesson[id] && isNew) {
				const { [id]: _, ...rest } = lesson;
				return {
					course: {
						...course,
						sections: newSections,
					},
					changes: {
						...changes,
						lesson: rest,
					},
					lastUpdated: new Date(),
				};
			}
			// if not new but have changed, delete in change and push to delete list
			let deleteItems = deleteLessons;
			if (!deleteLessons.includes(id) && !isNew) {
				deleteItems = [...deleteLessons, id];
			}

			if (lesson[id]) {
				const { [id]: _, ...rest } = lesson;
				console.log("DELETE LESSON NOT NEW BUT CHANGE", id, rest);

				return {
					course: {
						...course,
						sections: newSections,
					},
					changes: {
						...changes,
						lesson: rest,
					},
					deleteLessons: deleteItems,
					lastUpdated: new Date(),
				};
			}
			// if not new and not changed, push to delete list
			console.log("DELETE LESSON NOT NEW AND NOT CHANGE", id);

			return {
				course: {
					...course,
					sections: newSections,
				},
				deleteLessons: deleteItems,
				lastUpdated: new Date(),
			};
		});
	},
	updateLesson: (originPayload: LessonDTO) => {
		const { index, sectionId } = originPayload;

		const threadId = `${sectionId}-${index}`;
		if (sectionId && index >= 0 && !debounceMap.has(threadId)) {
			const debounceFunc = debounce((payload) => {
				set((state) => {
					const { changes, course } = state;
					const { lesson } = changes;

					const sections = course.sections || [];
					const { index, sectionId } = payload;

					const sectionToUpdate = sections.find(
						(item) => item.id === sectionId,
					);
					if (!sectionToUpdate || index === undefined || index < 0) {
						return state;
					}

					let lessonId;
					if (!sectionToUpdate.lessons) {
						sectionToUpdate.lessons = [];
					}
					if (sectionToUpdate.lessons[index]) {
						lessonId = sectionToUpdate.lessons[index].id;
						sectionToUpdate.lessons[index] = {
							...sectionToUpdate.lessons[index],
							...payload,
							id: lessonId,
							index: index,
						};
					} else {
						//
						lessonId = cuid();
						state.setCurrentLesson({
							...payload,
							id: lessonId,
							sectionId: sectionId,
						});
						payload.isNew = true;
						sectionToUpdate.lessons[index] = {
							...payload,
							id: lessonId,
						};
					}
					const newSection = sections.map((item) => {
						if (item.id === sectionId) {
							return sectionToUpdate;
						}
						return item;
					});

					const changesLesson = {
						...lesson,
						[lessonId]: { ...payload, id: lessonId },
					};

					return {
						// ...state,
						changes: {
							...changes,
							lesson: changesLesson,
						},
						course: {
							...course,
							sections: newSection,
						},
						lastUpdated: new Date(),
					};
				});
			}, 500);
			debounceMap.set(threadId, debounceFunc);
		}
		const debouncedProcessPayload = debounceMap.get(threadId);
		if (!debouncedProcessPayload) {
			return;
		}
		debouncedProcessPayload(originPayload);
	},

	updateLessons: (payload: LessonDTO[]) => {
		set((state) => {
			const { changes, course } = state;

			const { lesson } = changes;

			const sections = course.sections || [];
			const { index, sectionId } = payload[0];

			const sectionToUpdate = sections.find((item) => item.id === sectionId);
			if (!sectionToUpdate) {
				return state;
			}

			if (!sectionToUpdate.lessons) {
				sectionToUpdate.lessons = [];
			}

			payload.forEach((item) => {
				lesson[item.id] = item;
				sectionToUpdate.lessons[item.index] = item;
			});

			sectionToUpdate.lessons.sort((a, b) => a.index - b.index);
			const newSection = sections.map((item) => {
				if (item.id === sectionId) {
					return sectionToUpdate;
				}
				return item;
			});

			return {
				// ...state,
				changes: {
					...changes,
					lesson: lesson,
				},
				course: {
					...course,
					sections: newSection,
				},
				lastUpdated: new Date(),
			};
		});
	},
	saveDraft: () => {},

	setPreventDraft: (preventDraft: boolean) => {
		set({ preventDraft });
	},

	setIsSaving: (saving: boolean) => {
		set({ isSaving: saving });
	},

	updateProduct: (payload: IProductChange) => {
		set((state) => {
			return {
				// ...state,
				course: {
					...state.course,
					product: {
						...state.course.product,
						...payload,
					},
				},
				changes: {
					...state.changes,
					product: {
						...state.changes.product,
						...payload,
					},
				},
				lastUpdated: new Date(),
			};
		});
	},

	initCourse: (course) => {
		if (course) {
			// re-calculate section and lession index
			const sections = course.sections || [];

			sections.sort((a, b) => a.index - b.index);
			const newSections = sections.map((item, index) => {
				const lessons = item.lessons || [];
				lessons.sort((a, b) => a.index - b.index);
				const newLessons = lessons.map((lesson, lessonIndex) => {
					return {
						...lesson,
						index: lessonIndex,
					};
				});

				return {
					...item,
					index,
					lessons: newLessons,
				};
			});
			set((state) => ({
				course: {
					...course,
					sections: newSections,
				},
			}));
		} else {
			set((state) => ({
				course: {
					id: cuid(),
					name: "",
					description: "",
					coverImage: "",
					sections: [],
					videoCompletionRequired: false,
					startAt: "",
					status: OnlineCourseStatus.DRAFT,
					benefit: "[]",
					archived: "[]",
					questionAndAnswers: "[]",
					tags: "[]",
					product: {
						availableDays: undefined,
						price: undefined,
					},
				},
			}));
		}
	},
	resetChangeCourse: () => {
		set((state) => ({
			// ...state,
			changes: {
				...state.changes,
				course: null,
			},
		}));
	},
	resetChangeProduct: () => {
		set((state) => ({
			changes: {
				...state.changes,
				product: {},
			},
		}));
	},
	resetChangeSection: () => {
		set((state) => ({
			// ...state,
			changes: {
				...state.changes,
				section: {},
			},
			newSections: [],
			deleteSections: [],
		}));
	},
	resetChangeLesson: () => {
		set((state) => ({
			// ...state,
			changes: {
				...state.changes,
				lesson: {},
			},
			deleteLessons: [],
		}));
	},
	resetForm: () => {},
	setAction: (action: Action) => {
		set((state) => ({
			// ...state,
			action,
		}));
	},
	setActiveSection: (section: SectionDTO) => {
		set((state) => ({
			// ...state,
			activeSection: section,
		}));
	},
	setLastLessonIndex: (index: number) => {
		set((state) => ({
			// ...state,
			lastLessonIndex: index,
		}));
	},
	setCurrentLesson: (lesson: LessonDTO) => {
		set((state) => ({
			currentLesson: lesson,
		}));
	},
	handleDeleteLessonResource: (
		data: {
			lessonId: string;
			resourceId: string;
			isMain: boolean;
		}[],
	) => {
		set((state) => {
			const { deleteLessonResources } = state;
			const resourceIdentifiers = data.map(
				({ lessonId, resourceId, isMain }) =>
					`${lessonId}:${resourceId}:${isMain}`,
			);

			const deleteResources = deleteLessonResources.map((item) => {
				const { lessonId, resourceId, isMain } = item;
				return `${lessonId}:${resourceId}:${isMain}`;
			});

			const newDeleteResources = Array.from(
				new Set([...deleteResources, ...resourceIdentifiers]),
			);

			return {
				deleteLessonResources: newDeleteResources.map((item) => {
					const [lessonId, resourceId, isMain] = item.split(":");
					return {
						lessonId,
						resourceId,
						isMain: isMain === "true",
					};
				}),
			};
		});
	},
	resetDeleteLessonResources: (
		ids?: {
			lessonId: string;
			resourceId: string;
			isMain: boolean;
		}[],
	) => {
		set((state) => {
			if (ids) {
				return {
					deleteLessonResources: state.deleteLessonResources.filter(
						(item) =>
							!ids.some(
								({ lessonId, resourceId, isMain }) =>
									item.lessonId === lessonId &&
									item.resourceId === resourceId &&
									item.isMain === isMain,
							),
					),
				};
			}
			return {
				deleteLessonResources: [],
			};
		});
	},
	updateProductFromAPI: (product: ProductDTO) => {
		set((state) => {
			return {
				// ...state,
				course: {
					...state.course,
					product: product,
				},
			};
		});
	},
	registerTabState: (tab: OnlineCourseTab, state: any) => {
		set((store) => {
			const { tabStates } = store;
			return {
				tabStates: {
					...tabStates,
					[tab]: state,
				},
			};
		});
	},
	registerTabTrigger: (tab: OnlineCourseTab, trigger: any) => {
		set((store) => {
			const { tabTriggers } = store;
			return {
				tabTriggers: {
					...tabTriggers,
					[tab]: trigger,
				},
			};
		});
	},
	updateTabStatus: (tab: OnlineCourseTab, status: TabStatus) => {
		set((state) => {
			const { tabStatuses } = state;
			return {
				tabStatuses: {
					...tabStatuses,
					[tab]: status,
				},
			};
		});
	},
	updateLessonErrors: (lessonId: string, hasError: boolean) => {
		set((state) => {
			const { mapLessonErrors } = state;
			return {
				mapLessonErrors: {
					...mapLessonErrors,
					[lessonId]: hasError,
				},
			};
		});
	},
	updateSectionErrors: (sectionId: string, hasError: boolean) => {
		set((state) => {
			const { mapSectionErrors } = state;
			return {
				mapSectionErrors: {
					...mapSectionErrors,
					[sectionId]: hasError,
				},
			};
		});
	},
});
